export const PAID_FEE_RECEIPT_PAYMENT_STATUSES = ["Successful", "Settled"];

export const FEE_INSTALLMENT_PAID_WITHOUT_RECEIPT_MSG =
  "Please generate the fee receipt before completing this transaction.";

export const FEE_INSTALLMENT_ALREADY_PAID_MSG =
  "Fee is already paid. A fee receipt has been generated for this installment.";

export const NON_PAID_FEE_INSTALLMENT_STATUSES = [
  "Pending",
  "Overdue",
  "Cancelled",
];

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  return trimmed === "-" ? "" : trimmed;
};

export const isPaidFeeReceiptPaymentStatus = (paymentStatus) =>
  PAID_FEE_RECEIPT_PAYMENT_STATUSES.includes(
    String(paymentStatus ?? "").trim()
  );

export const installmentHasPaidFeeReceipt = (
  installmentId,
  studentFees = []
) => {
  const targetId = normalizeId(installmentId);
  if (!targetId) return false;

  return (Array.isArray(studentFees) ? studentFees : []).some((row) => {
    const rowInstallmentId = normalizeId(
      row?.organization_ems_fee_installment_id ??
        row?.installment?.organization_ems_fee_installment_id
    );
    if (rowInstallmentId !== targetId) return false;
    return isPaidFeeReceiptPaymentStatus(row?.payment_status);
  });
};

export const getPaidFeeReceiptInstallmentIds = (studentFees = []) => {
  const ids = new Set();
  (Array.isArray(studentFees) ? studentFees : []).forEach((row) => {
    if (!isPaidFeeReceiptPaymentStatus(row?.payment_status)) return;
    const installmentId = normalizeId(
      row?.organization_ems_fee_installment_id ??
        row?.installment?.organization_ems_fee_installment_id
    );
    if (installmentId) ids.add(installmentId);
  });
  return Array.from(ids);
};

export const isChangingFeeInstallmentStatusToPaid = ({
  nextStatus,
  initialStatus,
  mode,
}) => {
  if (String(nextStatus ?? "").trim() !== "Paid") return false;
  if (mode === "add") return true;
  return String(initialStatus ?? "").trim() !== "Paid";
};

export const validateFeeInstallmentPaidStatusRequiresReceipt = ({
  status,
  initialStatus,
  mode,
  installmentId,
  studentFees,
  paidInstallmentIds,
}) => {
  if (
    !isChangingFeeInstallmentStatusToPaid({
      nextStatus: status,
      initialStatus,
      mode,
    })
  ) {
    return "";
  }

  const paidIds = Array.isArray(paidInstallmentIds)
    ? paidInstallmentIds.map((id) => normalizeId(id)).filter(Boolean)
    : getPaidFeeReceiptInstallmentIds(studentFees);

  const targetId = normalizeId(installmentId);
  if (!targetId || !paidIds.includes(targetId)) {
    return FEE_INSTALLMENT_PAID_WITHOUT_RECEIPT_MSG;
  }

  return "";
};

export const isChangingFeeInstallmentStatusAwayFromPaidReceipt = ({
  nextStatus,
  installmentId,
  studentFees,
  paidInstallmentIds,
}) => {
  const normalizedNextStatus = String(nextStatus ?? "").trim();
  if (!NON_PAID_FEE_INSTALLMENT_STATUSES.includes(normalizedNextStatus)) {
    return false;
  }

  const targetId = normalizeId(installmentId);
  if (!targetId) return false;

  const paidIds = Array.isArray(paidInstallmentIds)
    ? paidInstallmentIds.map((id) => normalizeId(id)).filter(Boolean)
    : getPaidFeeReceiptInstallmentIds(studentFees);

  return paidIds.includes(targetId);
};

export const validateFeeInstallmentNonPaidStatusWhenReceiptExists = ({
  status,
  installmentId,
  studentFees,
  paidInstallmentIds,
}) => {
  if (
    !isChangingFeeInstallmentStatusAwayFromPaidReceipt({
      nextStatus: status,
      installmentId,
      studentFees,
      paidInstallmentIds,
    })
  ) {
    return "";
  }

  return FEE_INSTALLMENT_ALREADY_PAID_MSG;
};

export const validateFeeInstallmentStatusChange = ({
  status,
  initialStatus,
  mode,
  installmentId,
  studentFees,
  paidInstallmentIds,
}) =>
  validateFeeInstallmentNonPaidStatusWhenReceiptExists({
    status,
    installmentId,
    studentFees,
    paidInstallmentIds,
  }) ||
  validateFeeInstallmentPaidStatusRequiresReceipt({
    status,
    initialStatus,
    mode,
    installmentId,
    studentFees,
    paidInstallmentIds,
  });
